import React, { useState } from 'react';
import { MessageSquare, Bug, Lightbulb, HelpCircle, Heart, X, Send, Camera, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

const FEEDBACK_TYPES = [
  { id: 'bug', label: 'Bug', icon: Bug, color: 'text-red-400', bgColor: 'bg-red-500/20', borderColor: 'border-red-500/30' },
  { id: 'confusing', label: 'Confusing', icon: HelpCircle, color: 'text-amber-400', bgColor: 'bg-amber-500/20', borderColor: 'border-amber-500/30' },
  { id: 'idea', label: 'Idea', icon: Lightbulb, color: 'text-cyan-400', bgColor: 'bg-cyan-500/20', borderColor: 'border-cyan-500/30' },
  { id: 'praise', label: 'Praise', icon: Heart, color: 'text-pink-400', bgColor: 'bg-pink-500/20', borderColor: 'border-pink-500/30' }
];

export const FeedbackButton = ({ 
  childName = null, 
  childId = null,
  variant = 'floating' // 'floating' | 'inline' | 'header'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState(null);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!feedbackType || !message.trim()) {
      toast.error('Please select a type and enter a message');
      return;
    }

    setIsSubmitting(true);
    try {
      // Auto-capture context
      const browserInfo = `${navigator.userAgent.substring(0, 150)}`;
      const pageRoute = window.location.pathname;

      await api.post('/beta/feedback', {
        feedback_type: feedbackType,
        message: message.trim(),
        child_name: childName,
        child_id: childId,
        page_route: pageRoute,
        browser_info: browserInfo
      });

      toast.success('Thank you for your feedback! 💜');
      setIsOpen(false);
      setFeedbackType(null);
      setMessage('');
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error('Could not send feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Button variants
  const buttonStyles = {
    floating: 'fixed bottom-20 sm:bottom-20 left-4 sm:left-auto sm:right-6 z-50 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-full shadow-lg shadow-purple-500/25 flex items-center gap-2 transition-all hover:scale-105',
    inline: 'bg-slate-800/50 hover:bg-slate-700/50 text-slate-300 px-3 py-2 rounded-lg flex items-center gap-2 transition-all text-sm',
    header: 'bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all text-sm border border-indigo-500/30'
  };

  return (
    <>
      {/* Trigger Button */}
      <button 
        onClick={() => setIsOpen(true)}
        className={buttonStyles[variant]}
        data-testid="feedback-button"
      >
        <MessageSquare className="w-5 h-5" />
        <span className={variant === 'floating' ? 'hidden sm:inline' : ''}>
          {variant === 'floating' ? 'Feedback' : 'Send Feedback'}
        </span>
      </button>

      {/* Modal */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4"
          onClick={() => setIsOpen(false)}
        >
          <div 
            className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full p-6 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-purple-400" />
                  Beta Feedback
                </h2>
                <p className="text-sm text-slate-400 mt-1">
                  Help us improve Temple of Children
                </p>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-slate-400 hover:text-white p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Feedback Type Selection */}
            <div className="mb-4">
              <label className="text-sm text-slate-400 mb-2 block">What type of feedback?</label>
              <div className="grid grid-cols-2 gap-2">
                {FEEDBACK_TYPES.map(type => {
                  const Icon = type.icon;
                  const isSelected = feedbackType === type.id;
                  return (
                    <button
                      key={type.id}
                      onClick={() => setFeedbackType(type.id)}
                      className={`flex items-center gap-2 p-3 rounded-xl border transition-all ${
                        isSelected 
                          ? `${type.bgColor} ${type.borderColor} ${type.color}` 
                          : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:bg-slate-800'
                      }`}
                      data-testid={`feedback-type-${type.id}`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Message */}
            <div className="mb-4">
              <label className="text-sm text-slate-400 mb-2 block">Your message</label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder={
                  feedbackType === 'bug' ? "What happened? What did you expect?" :
                  feedbackType === 'confusing' ? "What was confusing? Where were you?" :
                  feedbackType === 'idea' ? "What would make this better?" :
                  feedbackType === 'praise' ? "What did you love?" :
                  "Tell us what's on your mind..."
                }
                className="w-full bg-slate-800/50 border border-slate-700 rounded-xl p-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-purple-500/50 min-h-[120px] resize-none"
                data-testid="feedback-message"
              />
            </div>

            {/* Context Info */}
            <div className="mb-6 p-3 bg-slate-800/30 rounded-lg text-xs text-slate-500">
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <span>📍 {window.location.pathname}</span>
                {childName && <span>👤 {childName}</span>}
                <span>🕐 {new Date().toLocaleString()}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setIsOpen(false)}
                className="flex-1 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !feedbackType || !message.trim()}
                className="flex-1 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="feedback-submit"
              >
                {isSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Feedback
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackButton;
